import type { TimelineItem } from '~/transformers/Dato/TimelineTransformer';

export type TripEdge = 'start' | 'end';

type GeoCoordinates = { latitude: number; longitude: number } | null;

const UK_BOUNDS = {
  minLatitude: 49.85,
  maxLatitude: 60.9,
  minLongitude: -8.65,
  maxLongitude: 1.78
};

const PASS_PAYMENT_STATUSES = ['INTERRAIL', 'EURAIL'];

const isPassPricedLeg = (item: Extract<TimelineItem, { type: 'Journey' }>): boolean => {
  const paymentStatus = item.paymentStatus?.toUpperCase();
  if (paymentStatus && PASS_PAYMENT_STATUSES.includes(paymentStatus)) return true;
  return item.ticketType === 'interrail' || item.ticketType === 'reservation';
};

const isUkCoordinate = (coordinates: GeoCoordinates): boolean => {
  if (!coordinates) return false;
  const { latitude, longitude } = coordinates;
  return (
    latitude >= UK_BOUNDS.minLatitude &&
    latitude <= UK_BOUNDS.maxLatitude &&
    longitude >= UK_BOUNDS.minLongitude &&
    longitude <= UK_BOUNDS.maxLongitude
  );
};

const getEdgeLocation = (timelineItems: TimelineItem[], edge: TripEdge): TimelineItem | undefined =>
  edge === 'start' ? timelineItems[0] : timelineItems[timelineItems.length - 1];

const getEdgeCoordinates = (location: TimelineItem | undefined, edge: TripEdge): GeoCoordinates => {
  if (!location) return null;
  if (location.type !== 'Journey') return location.geoCoordinates;
  return edge === 'start' ? location.originGeoCoordinates : location.destinationGeoCoordinates;
};

export const getTripEdgeCityName = (timelineItems: TimelineItem[], edge: TripEdge): string => {
  const location = getEdgeLocation(timelineItems, edge);
  if (!location) return '';
  if (location.type !== 'Journey') return location.title;
  return edge === 'start' ? location.origin : location.destination;
};

type ShouldShowInterrailUkTravelCalloutArgs = {
  isInterrail: boolean | null | undefined;
  date: string | null | undefined;
  timelineItems: TimelineItem[];
  edge: TripEdge;
};

export const shouldShowInterrailUkTravelCallout = ({
  isInterrail,
  date,
  timelineItems,
  edge
}: ShouldShowInterrailUkTravelCalloutArgs): boolean => {
  if (!isInterrail || !date) return false;
  if (!isUkCoordinate(getEdgeCoordinates(getEdgeLocation(timelineItems, edge), edge))) return false;

  return timelineItems.some((item) => {
    if (item.type !== 'Journey') return false;
    if (item.startDate !== date) return false;
    return isPassPricedLeg(item);
  });
};
